import type { DisconnectionDetails } from "@elevenlabs/client";
import { supabase } from "@/integrations/supabase/client";
import { foundMedia } from "@/lib/voice/foundMedia";
import { mediaLookups } from "@/lib/voice/mediaLookups";
import { ConversationRecorder } from "@/services/conversation/ConversationRecorder";
import { ConversationService } from "@/services/conversation/ConversationService";
import { flushConnectionLossLogs, recordConnectionLoss } from "@/services/integrations/elevenlabs/connectionLossLog";
import { ConversationMode, ConversationStatus } from "@/types/voice/conversation-state";
import { compareAttachments, formatLateMediaUpdate, type GenieAttachment } from "@/types/voice/genie-attachment";
import type {
  ContactDetailsRequestHandler,
  ContactDetailsRequestResult,
  SummaryCaptureValues,
} from "@/types/voice/summary-capture";
import {
  PHONE_TRANSFER_TOOL_NAME,
  REQUEST_CONTACT_DETAILS_TOOL_NAME,
  SHOW_RELATED_MEDIA_TOOL_NAME,
} from "@/utils/config/elevenlabs";

// Sent when the phone transfer is redirected to the browser handoff: the tool
// itself still reports an error, so the Genie must hear what really happened.
const PHONE_TRANSFER_REDIRECTED_UPDATE =
  "[SYSTEM CONTEXT - DO NOT READ ALOUD] The phone transfer cannot run in a website chat, so the visitor now sees a Connect me button that starts a live call with the team instead. Ignore the transfer error. Ask them to tap Connect me, and do not say they are connected yet or read out a phone number.";

// Shown in place of the Genie's own reply while that reply is held back.
const TRANSFER_REDIRECT_MESSAGE = "Tap Connect me to start a live call with the team.";

export type { ConversationStatus, ConversationMode };

export interface ConversationOverrides {
  agent?: {
    prompt?: {
      prompt?: string;
      llm?: string;
    };
    firstMessage?: string;
    language?: string;
  };
  tts?: {
    voiceId?: string;
    speed?: number;
    stability?: number;
    similarityBoost?: number;
  };
  conversation?: {
    textOnly?: boolean;
  };
  turn?: {
    turn_timeout?: number;
    silence_end_call_timeout?: number;
  };
}

export class VoiceAgentService {
  private conversation: any = null;
  private onStatusChangeCallback: ((status: ConversationStatus) => void) | null = null;
  private onModeChangeCallback: ((mode: ConversationMode) => void) | null = null;
  private onMessageCallback: ((message: string, isUser: boolean) => void) | null = null;
  private onDisconnectCallback: ((conversationId: string | null) => Promise<void>) | null = null;
  private onHumanHandoffRequestedCallback: (() => void) | null = null;
  private onContactDetailsRequestedCallback: ContactDetailsRequestHandler | null = null;
  private onAttachmentCallback: ((attachment: GenieAttachment, replacesUrl?: string) => void) | null = null;
  private onMuteChangeCallback: ((isMuted: boolean) => void) | null = null;
  private onVadScoreCallback: ((score: number) => void) | null = null;
  private currentStatus: ConversationStatus = "disconnected";
  private currentMode: ConversationMode = "idle";
  private agentId: string;
  private userId: string | undefined;
  private email: string | undefined;
  private userName: string | undefined;
  private userMemory: string | null = null;
  // The genie's goals text, built on the server and returned with the signed URL.
  private conversationContext: string | null = null;
  // Set when the genie's multi-language setting is off; null lets it follow the caller.
  private responseLanguage: string | null = null;
  private defaultVolume: number = 0.85;
  private keepAliveInterval: NodeJS.Timeout | null = null;
  private conversationRecorded: boolean = false;
  private connectedAt: number | null = null;
  private overrides: ConversationOverrides | undefined;
  private thinkingTimeout: NodeJS.Timeout | null = null;
  private processingTimeout: NodeJS.Timeout | null = null;
  private searchingTimeout: NodeJS.Timeout | null = null;
  private retrievingTimeout: NodeJS.Timeout | null = null;
  private isMutedState: boolean = false;
  private holdingForTransferRedirect: boolean = false;
  private skipMicrophonePermission: boolean = false;
  // True when this session is text-only. Drives the realtime client onto a
  // text transport that never touches the microphone or audio worklets, so it
  // connects reliably inside the embedded widget iframe (where the audio stack
  // is autoplay-suspended / permission-restricted and otherwise stalls).
  private isTextOnly: boolean = false;
  private originalGetUserMedia: any = null;
  private isQuickTest: boolean = false;
  // User messages typed before the realtime session reaches "connected" are
  // buffered here and flushed on connect, so a text message sent immediately
  // after startConversation() is never silently dropped by the status guard.
  private pendingUserMessages: string[] = [];
  // Rolling window of the conversation, sent with showRelatedMedia so the
  // resolver judges the topic in context rather than from a few words.
  private recentTurns: Array<{ role: "user" | "agent"; message: string }> = [];
  private mediaRequestCount: number = 0;
  private static readonly RECENT_TURNS_LIMIT = 8;
  private static readonly MAX_MEDIA_REQUESTS_PER_SESSION = 10;
  // Cards one share puts in the chat; the rest of what it finds is listed in the Resources sheet.
  private static readonly MAX_MEDIA_IN_CHAT = 3;
  // How long the agent waits for a lookup before carrying on. A slower lookup
  // still lands on screen when it finishes (see showRelatedMedia) — the agent
  // just isn't held silent for it.
  private static readonly MEDIA_WAIT_MS = 7000;
  private static readonly MEDIA_NOT_FOUND = "not_found";
  // The replies are what the Genie believes happened: the form only collects
  // details once the visitor sends it, so none of them claims a lead.
  private static readonly CONTACT_DETAILS_REPLIES: Record<ContactDetailsRequestResult | "unavailable", string> = {
    shown:
      "form_shown: the visitor now sees a short contact form in the chat, filled in with anything you passed. " +
      "Tell them in one short sentence that it is there so the team can follow up. Do not ask them to say or type their details in the conversation.",
    already_captured:
      "already_captured: the business already has this visitor's contact details. Do not ask for them again; carry on helping.",
    declined:
      "declined: the visitor closed the contact form earlier in this conversation. Do not ask for their details again; carry on helping.",
    unavailable:
      "unavailable: no contact form can be shown here. Carry on helping without asking for their details.",
  };
  private static readonly CONTACT_DETAIL_MAX_LENGTH = { name: 100, email: 254, phone: 40 } as const;
  private static readonly MEDIA_STILL_LOOKING =
    "still_looking: the lookup is taking a moment. If something relevant turns up it will appear in the chat on its own. " +
    "Carry on without promising it, and do not call the tool again for this topic unless the visitor asks for it again.";
  private static readonly THINKING_DELAY_MS = 200;
  private static readonly PROCESSING_DELAY_MS = 1500;
  private static readonly SEARCHING_DELAY_MS = 3500;
  private static readonly RETRIEVING_DELAY_MS = 6000;

  constructor(
    agentId: string,
    userId?: string,
    email?: string,
    userName?: string,
    overrides?: ConversationOverrides,
    userMemory?: string | null,
    isQuickTest?: boolean,
  ) {
    if (!agentId) {
      throw new Error("Agent ID is required");
    }
    this.agentId = agentId;
    this.userId = userId;
    this.email = email;
    this.userName = userName;
    this.userMemory = userMemory || null;
    this.isQuickTest = isQuickTest || false;
    this.setOverrides(overrides);
  }

  public setResponseLanguage(language: string | null): void {
    this.responseLanguage = language;
  }

  public setOverrides(overrides: ConversationOverrides | undefined): void {
    this.overrides = overrides;

    // If textOnly is enabled, automatically set firstMessage to "" and skipMicrophonePermission to true
    if (overrides?.conversation?.textOnly) {
      if (!this.overrides.agent) {
        this.overrides.agent = {};
      }
      this.overrides.agent.firstMessage = "";
      this.skipMicrophonePermission = true;
    } else {
      this.skipMicrophonePermission = false;
    }
  }

  public setSkipMicrophonePermission(skip: boolean): void {
    this.skipMicrophonePermission = skip;
  }

  public setUserMemory(memory: string | null): void {
    this.userMemory = memory;
  }

  private async getSignedUrl(): Promise<string | null> {
    try {
      const response = await ConversationService.getSignedUrl(this.agentId);
      const signedUrl = response?.signed_url;
      if (typeof signedUrl !== "string" || !/^wss?:\/\//.test(signedUrl)) {
        console.error("Invalid signed URL received:", signedUrl);
        return null;
      }
      this.conversationContext = response?.conversation_context || null;
      return signedUrl;
    } catch (error) {
      console.error("Error getting signed URL:", error);
      return null;
    }
  }

  public async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      window.persistedAudioStream = stream;
      console.log("Microphone permission granted");
      return true;
    } catch (error) {
      console.error("Microphone permission error:", error);
      return false;
    }
  }

  public async startConversation(): Promise<boolean> {
    try {
      if (this.conversation) {
        console.log("[VoiceAgent] Conversation already active, ending previous session");
        await this.endConversation();
      }

      console.log("[VoiceAgent] skipMicrophonePermission flag:", this.skipMicrophonePermission);

      // For text-only mode, intercept getUserMedia to prevent browser permission prompt
      if (this.skipMicrophonePermission) {
        console.log("[VoiceAgent] Text-only mode: intercepting getUserMedia");
        this.originalGetUserMedia = navigator.mediaDevices.getUserMedia;
        navigator.mediaDevices.getUserMedia = async () => {
          console.log("[VoiceAgent] getUserMedia intercepted in text-only mode, returning silent audio stream");
          // Create a silent audio context to generate a dummy audio track
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const destination = audioContext.createMediaStreamDestination();
          return destination.stream;
        };
      }

      // Skip microphone permission for text-only mode
      if (!this.skipMicrophonePermission) {
        const micAccessGranted = await this.requestMicrophonePermission();
        if (!micAccessGranted) {
          console.error("Microphone access was denied");
          return false;
        }
      } else {
        console.log("[VoiceAgent] Skipping microphone permission for text-only mode");
      }

      // Start from a clean buffer so a previous session's unsent text never
      // carries over into this one.
      this.pendingUserMessages = [];
      this.recentTurns = [];
      this.mediaRequestCount = 0;
      mediaLookups.clear();
      foundMedia.clear();

      this.updateStatus("connecting");

      const { Conversation } = await import("@elevenlabs/client");

      const signedUrl = await this.getSignedUrl();
      if (!signedUrl) {
        console.error("Failed to get signed URL");
        this.updateStatus("disconnected");
        // Restore getUserMedia if we intercepted it
        if (this.originalGetUserMedia) {
          navigator.mediaDevices.getUserMedia = this.originalGetUserMedia;
          this.originalGetUserMedia = null;
        }
        return false;
      }

      //console.log("Starting conversation with agent ID:", this.agentId);
      console.log("User ID for conversation:", this.userId ? this.userId : "none");
      console.log("Overrides for conversation:", this.overrides);

      const sessionConfig: any = {
        signedUrl,
        ...(this.overrides && { overrides: this.overrides }),
        clientTools: {
          // If in text-only mode, don't request microphone access
          ...(this.skipMicrophonePermission && { enableMicrophone: false }),
          // The reply is what the Genie believes happened, so it must not claim
          // a call: at this point the visitor has only been shown a prompt.
          requestHumanHandoff: async () => {
            if (!this.onHumanHandoffRequestedCallback) {
              return "unavailable: a live call cannot be started here. Offer to take the visitor's contact details instead.";
            }
            this.onHumanHandoffRequestedCallback();
            return "confirmation_shown: the visitor now sees a prompt asking them to confirm the call. No call has started yet. Ask them to tap Connect me, and do not say they are connected.";
          },
          [SHOW_RELATED_MEDIA_TOOL_NAME]: (parameters: { topic?: unknown; context?: unknown }) =>
            this.showRelatedMedia(parameters),
          [REQUEST_CONTACT_DETAILS_TOOL_NAME]: (parameters: Record<string, unknown>) =>
            this.requestContactDetails(parameters),
        },
        // A genie with transfer numbers has the phone transfer tool, which
        // only works on phone calls. When it reaches for it in a website chat,
        // start the browser handoff instead, so the visitor gets a person no
        // matter which tool the Genie picked. Needs agent_tool_request among
        // the genie's client_events (DEFAULT_CLIENT_EVENTS).
        onAgentToolRequest: (request: { tool_name?: string } | undefined) => {
          if (request?.tool_name !== PHONE_TRANSFER_TOOL_NAME || !this.onHumanHandoffRequestedCallback) return;
          this.onHumanHandoffRequestedCallback();
          this.sendContextualUpdate(PHONE_TRANSFER_REDIRECTED_UPDATE);
          void this.holdForTransferRedirect();
        },
        onConnect: () => {
          this.updateStatus("connected");
          this.connectedAt = Date.now();
          void flushConnectionLossLogs();
          // Deliver any messages the user typed while the session was still
          // connecting. Without this, the first text message is dropped.
          this.flushPendingMessages();
          const conversationId = this.getConversationId();
          if (conversationId) {
            this.logEvent("start", "success", "User clicked 'Start conversation button", conversationId);
          }
        },
        onDisconnect: async (details?: DisconnectionDetails) => {
          const conversationId = this.getConversationId();
          const modeAtDisconnect = this.currentMode;
          this.updateStatus("disconnected");
          this.updateMode("idle");
          // Drop any unflushed messages so they can't leak into a later session.
          this.pendingUserMessages = [];
          this.holdingForTransferRedirect = false;
          // Restore getUserMedia if we intercepted it
          if (this.originalGetUserMedia) {
            navigator.mediaDevices.getUserMedia = this.originalGetUserMedia;
            this.originalGetUserMedia = null;
            console.log("[VoiceAgent] Restored original getUserMedia");
          }
          if (conversationId) {
            this.logDisconnect(details, modeAtDisconnect, conversationId);
          }
          if (this.onDisconnectCallback) {
            await this.onDisconnectCallback(conversationId);
          }
        },
        onMessage: (props: any) => {
          // Record conversation on first message when we have a conversationId
          if (!this.conversationRecorded) {
            const conversationId = this.getConversationId();
            if (conversationId) {
              this.conversationRecorded = true;
              ConversationRecorder.recordConversationStart(
                this.agentId,
                conversationId,
                this.userId || "",
                this.isQuickTest,
              );
            }
          }

          if (!this.onMessageCallback) {
            console.warn(
              "[VoiceAgent] onMessage event arrived but no listener is bound yet — message will be dropped",
              props,
            );
            return;
          }
          if (!props || typeof props.message !== "string" || props.message.length === 0) {
            // SDK sometimes emits events without text (audio chunks, internal
            // states). Skipping these is correct, but log so it's not silent.
            return;
          }
          const isUserMessage = props.source === "user";
          if (this.holdingForTransferRedirect) {
            // The Genie's reply to the failed transfer contradicts the Connect
            // me prompt on screen; it stays hidden until the visitor answers.
            if (!isUserMessage) return;
            void this.releaseTransferRedirectHold();
          }
          this.recentTurns = [
            ...this.recentTurns,
            { role: isUserMessage ? ("user" as const) : ("agent" as const), message: props.message },
          ].slice(-VoiceAgentService.RECENT_TURNS_LIMIT);
          this.onMessageCallback(props.message, isUserMessage);
        },
        onError: (error: any) => {
          console.error("Conversation error - Full details:", error);
          console.error("Conversation error - Type:", typeof error);
          console.error("Conversation error - String:", String(error));
          if (error && error.message) {
            console.error("Conversation error - Message:", error.message);
          }
          if (error && error.stack) {
            console.error("Conversation error - Stack:", error.stack);
          }

          // Check if it's a transcription-related error
          if (
            error &&
            (String(error).includes("transcription") ||
              String(error).includes("audio") ||
              String(error).includes("microphone"))
          ) {
            console.error("Transcription/Audio error detected:", error);
          }

          const conversationId = this.getConversationId();
          if (conversationId) {
            const errorData = {
              type: typeof error,
              message: error?.message || String(error),
              stack: error?.stack,
            };
            this.logEvent("error", "fail", JSON.stringify(errorData), conversationId);
          }

          this.updateStatus("disconnected");
        },
        onStatusChange: (prop: any) => {
          if (prop.type === "recording" && prop.value === true) {
            // User started speaking - clear all pending state transitions
            this.clearAllModeTimeouts();
            this.updateMode("listening");
          } else if (prop.type === "recording" && prop.value === false) {
            // User stopped speaking - start escalation chain
            this.clearAllModeTimeouts();
            this.updateMode("idle");

            // Escalate through thinking states based on elapsed time
            this.thinkingTimeout = setTimeout(() => {
              if (this.currentMode !== "speaking") {
                this.updateMode("thinking");
              }
            }, VoiceAgentService.THINKING_DELAY_MS);

            this.processingTimeout = setTimeout(() => {
              if (this.currentMode !== "speaking") {
                this.updateMode("processing");
              }
            }, VoiceAgentService.PROCESSING_DELAY_MS);

            this.searchingTimeout = setTimeout(() => {
              if (this.currentMode !== "speaking") {
                this.updateMode("searching");
              }
            }, VoiceAgentService.SEARCHING_DELAY_MS);

            this.retrievingTimeout = setTimeout(() => {
              if (this.currentMode !== "speaking") {
                this.updateMode("retrieving");
              }
            }, VoiceAgentService.RETRIEVING_DELAY_MS);
          }
          if (prop.type === "speaking" && prop.value === true) {
            // Agent started speaking - clear all pending transitions immediately
            this.clearAllModeTimeouts();
            this.updateMode("speaking");
          } else if (prop.type === "speaking" && prop.value === false) {
            this.updateMode("idle");
          }
          if (prop.status) {
            this.updateStatus(prop);
          }
        },
        onModeChange: (mode: any) => {
          this.updateMode(mode);
        },
        onVadScore: ({ vadScore }: { vadScore: number }) => {
          this.onVadScoreCallback?.(vadScore);
        },
      };

      this.conversation = await Conversation.startSession(sessionConfig);
      // A textOnly session is a TextConversation: no microphone, no audio out.
      this.isTextOnly = !!this.overrides?.conversation?.textOnly;

      //console.log("Conversation created:", this.conversation);

      // Send system context immediately after connection
      this.sendInitialSystemContext();

      // Belt-and-suspenders: if onConnect already fired before this.conversation
      // was assigned, flush any buffered messages now that it exists.
      this.flushPendingMessages();

      // Start keep-alive mechanism to prevent browser suspension
      this.startKeepAlive();

      return true;
    } catch (error) {
      console.error("Failed to start conversation - Full details:", error);
      console.error("Failed to start conversation - Type:", typeof error);
      console.error("Failed to start conversation - String:", String(error));
      if (error && error.message) {
        console.error("Failed to start conversation - Message:", error.message);
      }
      if (error && error.stack) {
        console.error("Failed to start conversation - Stack:", error.stack);
      }

      // Check for specific error types
      if (String(error).includes("permission") || String(error).includes("microphone")) {
        console.error("Microphone permission error detected");
      }
      if (String(error).includes("transcription")) {
        console.error("Transcription error detected");
      }
      if (String(error).includes("websocket") || String(error).includes("connection")) {
        console.error("Connection error detected");
      }

      // Log start failure - use a placeholder ID since conversation didn't establish
      const errorData = {
        type: typeof error,
        message: error?.message || String(error),
        stack: error?.stack,
      };
      // Create a temporary ID for logging purposes (using timestamp)
      const tempConversationId = `failed_${Date.now()}`;
      this.logEvent("start", "fail", JSON.stringify(errorData), tempConversationId);

      this.updateStatus("disconnected");
      return false;
    }
  }

  public async endConversation(): Promise<void> {
    // Stop keep-alive mechanism
    this.stopKeepAlive();
    // Clear all pending mode timeouts
    this.clearAllModeTimeouts();
    // Discard any messages that never made it to the realtime channel.
    this.pendingUserMessages = [];
    mediaLookups.clear();

    if (this.conversation) {
      try {
        await this.conversation.endSession();

        // Ensure conversation reference is cleared immediately
        this.conversation = null;
        this.conversationRecorded = false;

        // Stop and release the microphone stream
        if (window.persistedAudioStream) {
          window.persistedAudioStream.getTracks().forEach((track) => {
            track.stop();
          });
          window.persistedAudioStream = null;
        }

        // Reset overrides and flags after conversation ends
        this.overrides = undefined;
        this.skipMicrophonePermission = false;
        this.isTextOnly = false;

        this.updateStatus("disconnected");
        this.updateMode("idle");
        console.log("Conversation ended successfully");
      } catch (error) {
        console.error("Error ending conversation:", error);
        this.conversation = null;
        this.conversationRecorded = false;
        // Reset overrides and flags on error as well
        this.overrides = undefined;
        this.skipMicrophonePermission = false;
        this.isTextOnly = false;
        this.updateStatus("disconnected");
        this.updateMode("idle");
      }
    }
  }

  private logDisconnect(
    details: DisconnectionDetails | undefined,
    mode: ConversationMode,
    conversationId: string,
  ): void {
    if (details?.reason !== "error") {
      const description =
        details?.reason === "agent" ? "The conversation was ended by the Genie" : "User clicked 'End conversation button";
      this.logEvent("end", "success", description, conversationId);
      return;
    }

    void recordConnectionLoss({
      el_conv_id: conversationId,
      el_agent_id: this.agentId,
      email: this.email,
      user_id: this.userId,
      payload: JSON.stringify({
        message: details.message,
        closeCode: details.closeCode,
        closeReason: details.closeReason,
        contextType: details.context?.type,
        browserOnline: navigator.onLine,
        pageVisibility: document.visibilityState,
        mode,
        secondsConnected: this.connectedAt ? Math.round((Date.now() - this.connectedAt) / 1000) : null,
        occurredAt: new Date().toISOString(),
      }),
    });
  }

  private async logEvent(
    action: string,
    status: string,
    payload: string | null,
    conversationId: string,
  ): Promise<void> {
    supabase.functions
      .invoke("logger", {
        body: {
          action: "conversation",
          data: {
            el_conv_id: conversationId,
            action,
            status,
            payload,
            el_agent_id: this.agentId,
            email: this.email,
            user_id: this.userId,
          },
        },
      })
      .catch((error) => {
        console.warn("Failed to log conversation event:", error);
      });
  }

  private async logSystemContext(contextPayload: string, conversationId: string): Promise<void> {
    supabase.functions
      .invoke("logger", {
        body: {
          action: "conversation",
          data: {
            el_conv_id: conversationId,
            action: "system_context",
            status: "success",
            payload: contextPayload,
            el_agent_id: this.agentId,
            email: this.email,
            user_id: this.userId,
          },
        },
      })
      .catch((error) => {
        console.warn("Failed to log system context:", error);
      });
  }

  public async setVolume(volume: number): Promise<void> {
    // A text conversation has no audio output to set.
    if (this.conversation && !this.isTextOnly) {
      try {
        const normalizedVolume = Math.max(0, Math.min(1, volume));
        console.log(`Setting volume to ${normalizedVolume}`);
        // SDK's setVolume only stores a property; the actual GainNode is
        // only refreshed when the next audio chunk arrives. That means a
        // mute mid-sentence still plays the buffered audio out. Apply the
        // gain directly so volume changes take effect immediately.
        this.conversation.setVolume({ volume: normalizedVolume });
        this.applyOutputGainImmediate(normalizedVolume);
        // When muting hard, also flush any in-flight audio buffer so the
        // agent doesn't finish its current sentence on the local speakers.
        if (normalizedVolume === 0) {
          this.interruptCurrentSpeech();
        }
      } catch (error) {
        console.error("Error setting volume:", error);
      }
    }
  }

  /**
   * Dump the audio worklet's buffered output so the agent's current
   * sentence stops playing locally. Pairs with setVolume(0) to make the
   * "Mute Genie" / "Pause" buttons feel like a real phone hold.
   *
   * Reaches into ElevenLabs SDK internals (conversation.output.worklet);
   * wrapped in try/catch so it degrades safely if the SDK shape changes.
   */
  public interruptCurrentSpeech(): void {
    const conv = this.conversation as {
      output?: { worklet?: { port?: { postMessage: (msg: unknown) => void } } };
    } | null;
    const port = conv?.output?.worklet?.port;
    if (!port) return;
    try {
      port.postMessage({ type: "interrupt" });
      port.postMessage({ type: "clearInterrupted" });
    } catch (error) {
      console.warn("Could not flush audio output buffer:", error);
    }
  }

  private applyOutputGainImmediate(volume: number): void {
    const conv = this.conversation as {
      output?: { gain?: { gain?: { value: number } } };
    } | null;
    const gainParam = conv?.output?.gain?.gain;
    if (!gainParam) return;
    try {
      gainParam.value = volume;
    } catch (error) {
      console.warn("Could not apply output gain immediately:", error);
    }
  }

  // The phone transfer always fails in a website chat and the Genie then says
  // so, which reads as a dead end beside the Connect me prompt. Silence it and
  // show a fixed line instead, until the visitor next says something.
  private async holdForTransferRedirect(): Promise<void> {
    if (this.holdingForTransferRedirect) return;
    this.holdingForTransferRedirect = true;
    this.onMessageCallback?.(TRANSFER_REDIRECT_MESSAGE, false);
    await this.setVolume(0);
  }

  private async releaseTransferRedirectHold(): Promise<void> {
    this.holdingForTransferRedirect = false;
    this.interruptCurrentSpeech();
    if (!this.isMutedState) await this.setVolume(this.defaultVolume);
  }

  public async setMicMuted(muted: boolean): Promise<void> {
    // A text conversation never opens the microphone.
    if (!this.conversation || this.isTextOnly) return;
    try {
      console.log(`Setting mic muted to ${muted}`);
      const anyConversation = this.conversation as { setMicMuted?: (v: boolean) => Promise<void> | void };
      if (typeof anyConversation.setMicMuted === "function") {
        await anyConversation.setMicMuted(muted);
      } else {
        console.warn("Conversation.setMicMuted is not available on this client");
      }
    } catch (error) {
      console.error("Error setting mic muted:", error);
    }
  }

  public onVadScore(callback: ((score: number) => void) | null): void {
    this.onVadScoreCallback = callback;
  }

  public getInputVolume(): number {
    if (!this.conversation) return 0;
    const c = this.conversation as { getInputVolume?: () => number };
    if (typeof c.getInputVolume === "function") {
      return c.getInputVolume();
    }
    return 0;
  }

  public async mute(): Promise<void> {
    if (!this.conversation || this.isMutedState) return;

    try {
      // Hard-cut agent output: store property, snap gain to 0, flush buffer.
      await this.setVolume(0);

      // Mute microphone input using ElevenLabs SDK method
      this.conversation.setMicMuted(true);

      this.isMutedState = true;
      console.log("Conversation muted - agent volume off, microphone muted");
      if (this.onMuteChangeCallback) {
        this.onMuteChangeCallback(true);
      }
    } catch (error) {
      console.error("Error muting conversation:", error);
    }
  }

  public async unmute(): Promise<void> {
    if (!this.conversation || !this.isMutedState) return;

    try {
      // Flush stale audio queued during mute, then restore volume + gain.
      this.interruptCurrentSpeech();
      await this.setVolume(this.defaultVolume);

      // Unmute microphone input using ElevenLabs SDK method
      this.conversation.setMicMuted(false);

      this.isMutedState = false;
      console.log("Conversation unmuted - agent volume restored, microphone active");
      if (this.onMuteChangeCallback) {
        this.onMuteChangeCallback(false);
      }
    } catch (error) {
      console.error("Error unmuting conversation:", error);
    }
  }

  public async toggleMute(): Promise<boolean> {
    if (this.isMutedState) {
      await this.unmute();
    } else {
      await this.mute();
    }
    return this.isMutedState;
  }

  public async muteMicrophoneOnly(): Promise<void> {
    if (!this.conversation) return;

    try {
      this.conversation.setMicMuted(true);
      console.log("Microphone muted - agent volume unchanged");
    } catch (error) {
      console.error("Error muting microphone:", error);
    }
  }

  public async unmuteMicrophoneOnly(): Promise<void> {
    if (!this.conversation) return;

    try {
      this.conversation.setMicMuted(false);
      console.log("Microphone unmuted - agent volume unchanged");
    } catch (error) {
      console.error("Error unmuting microphone:", error);
    }
  }

  public isMuted(): boolean {
    return this.isMutedState;
  }

  public onMuteChange(callback: (isMuted: boolean) => void): void {
    this.onMuteChangeCallback = callback;
  }

  public getConversationId(): string | null {
    if (this.conversation) {
      if (typeof this.conversation.getId === "function") {
        const id = this.conversation.getId();
        if (id) {
          //console.log(`Found conversation ID via getId():`, id);
          return id;
        }
      }

      if (this.conversation.connection && this.conversation.connection.conversationId) {
        const id = this.conversation.connection.conversationId;
        //console.log(`Found conversation ID in connection:`, id);
        return id;
      }
    }

    return null;
  }

  public sendUserMessage(text: string): void {
    // Typed messages never come back through onMessage, so a reply to one
    // must lift the transfer-redirect hold here.
    if (this.holdingForTransferRedirect) void this.releaseTransferRedirectHold();
    if (this.conversation && this.currentStatus === "connected") {
      console.log("Sending user message:", text);
      this.conversation.sendUserMessage(text);
      return;
    }

    // Session is still establishing — buffer the message and let onConnect
    // flush it, instead of silently dropping the user's first text.
    if (this.currentStatus === "connecting" || this.conversation) {
      console.log("Queueing user message until connected:", text);
      this.pendingUserMessages.push(text);
      return;
    }

    // No session at all: this is a real failure, not a timing race. Record it
    // server-side so the regression is observable instead of vanishing into a
    // console warning. (Internal logging only — never surfaced to end users.)
    console.error("[VoiceAgent] Dropped user message — no active conversation to send to");
    const failureId = this.getConversationId() || `no_session_${Date.now()}`;
    this.logEvent(
      "text_message",
      "fail",
      JSON.stringify({ reason: "no_active_conversation", text_length: text.length }),
      failureId,
    );
  }

  // Flush buffered user messages once the realtime session is connected.
  // Idempotent: safe to call from multiple connect paths; clears as it sends.
  private flushPendingMessages(): void {
    if (!this.conversation || this.currentStatus !== "connected") {
      return;
    }
    if (this.pendingUserMessages.length === 0) {
      return;
    }
    const queued = this.pendingUserMessages.splice(0, this.pendingUserMessages.length);
    for (const message of queued) {
      try {
        console.log("Flushing queued user message:", message);
        this.conversation.sendUserMessage(message);
      } catch (error) {
        console.warn("Failed to flush queued user message:", error);
      }
    }
  }

  public sendContextualUpdate(text: string): void {
    // Any later update from the page (the visitor cancelled, or the call
    // failed) means the Genie has something real to say again.
    if (this.holdingForTransferRedirect && text !== PHONE_TRANSFER_REDIRECTED_UPDATE) {
      void this.releaseTransferRedirectHold();
    }
    if (this.conversation && this.currentStatus === "connected") {
      console.log("Sending contextual update:", text);
      this.conversation.sendContextualUpdate(text);
    } else {
      console.warn("Cannot send contextual update: conversation not connected");
    }
  }

  public sendUserActivity(): void {
    if (this.conversation && this.currentStatus === "connected") {
      this.conversation.sendUserActivity();
    }
  }

  private sendInitialSystemContext(): void {
    const contextParts = ["[SYSTEM CONTEXT - DO NOT READ ALOUD]"];
    const dataContextParts = [];

    const now = new Date();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localDateTime = now.toLocaleString("en-US", {
      timeZone: userTimezone,
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
    dataContextParts.push(`Current Date & Time: ${localDateTime}`);

    if (this.userName) {
      dataContextParts.push(`User Name: ${this.userName}`);
    }

    if (this.userId) {
      dataContextParts.push(`User ID: ${this.userId}`);
    }

    if (this.email) {
      dataContextParts.push(`Email: ${this.email}`);
    }

    contextParts.push(...dataContextParts);

    if (this.responseLanguage) {
      // Multi-language is switched off for this genie.
      contextParts.push(
        "\n[LANGUAGE - CRITICAL]",
        `Always respond in ${this.responseLanguage}, even when the user speaks or writes in another language. Never switch languages.`,
      );
    } else {
      contextParts.push(
        "\n[LANGUAGE DETECTION - CRITICAL]",
        // "Always respond in the same language the user is using.",
        // "If the user switches languages, switch with them.",
        "Switch language when the caller asks for another language (for example 'can we speak Mandarin?' or 'hablas español?'), " +
          "or speaks two or more full sentences in it. " +
          "Never switch because of a single foreign word, a filler sound such as 'ja', 'äh' or 'um', a name, or an accent. " +
          "When unsure, stay in the current language.",
      );
    }

    // HG-4793: platform rules that every genie follows, injected per session so
    // genies whose stored prompt predates them behave the same on the web.
    contextParts.push(
      "\n[SPEAKING STYLE - CRITICAL]",
      "When you say an email address out loud, say it once the way a person would (for example 'tim at helpgenie dot com'). Never spell it out letter by letter unless the person asks you to.",
      "When someone wants to book, enquire or request something, collect the details the team needs first (what they want, when, how many people, their name and the best way to reach them) before you point them to a web page or say the team will be in touch.",
    );

    // Only a session with a screen to put cards on gets the hint — phone calls
    // and screen-less surfaces never do.
    if (this.onAttachmentCallback) {
      contextParts.push(
        "\n[ON-SCREEN MEDIA - DO NOT READ ALOUD]",
        `The visitor can see a chat window, and the ${SHOW_RELATED_MEDIA_TOOL_NAME} tool puts links and pictures from this business's own resources into it. ` +
          `Call it whenever the visitor asks where to buy or find something, asks for a shop, a link, a page, options or recommendations to look at, or asks to see something, and whenever what you are describing could be illustrated (a product, a page, a document, a place). ` +
          `Never tell the visitor you cannot share links: call the tool instead. ` +
          `Pass the broad subject as the topic (for example "skincare gifts" or "kitchen gifts"), not a detailed product specification. ` +
          `If it answers not_found, say nothing suitable is on hand for that and carry on; a later request on another subject must still call the tool. Never read web addresses aloud.`,
      );
    }

    // Only a session that can start a live call gets the hint — phone calls
    // never run through this service.
    if (this.onHumanHandoffRequestedCallback) {
      contextParts.push(
        "\n[CONNECTING TO A PERSON - DO NOT READ ALOUD]",
        "This is a website chat, not a phone call. When the visitor wants to talk to a person, use your tool for connecting them; here it shows them a Connect me button that starts a live call with the team. " +
          "Do not read out a phone number instead of connecting them, and do not say a connection is unavailable.",
      );
    }

    // Include user memory context if available
    if (this.userMemory) {
      contextParts.push("\n[USER CONTEXT FROM PREVIOUS CONVERSATIONS]", this.userMemory);
      console.log("[VoiceAgent] Injected user memory into session context");
    }

    if (this.conversationContext) {
      contextParts.push(`\n${this.conversationContext}`);
    }

    const systemContext = contextParts.join("\n");
    this.sendContextualUpdate(systemContext);

    const conversationId = this.getConversationId();
    if (conversationId) {
      this.logSystemContext(systemContext, conversationId);
    }
  }

  public isConnected(): boolean {
    return this.currentStatus === "connected";
  }

  public getStatus(): ConversationStatus {
    return this.currentStatus;
  }

  public getMode(): ConversationMode {
    return this.currentMode;
  }

  public onStatusChange(callback: (status: ConversationStatus) => void): void {
    this.onStatusChangeCallback = callback;
    // Immediately call with current status
    if (callback) callback(this.currentStatus);
  }

  public onModeChange(callback: (mode: ConversationMode) => void): void {
    this.onModeChangeCallback = callback;
    // Immediately call with current mode
    if (callback) callback(this.currentMode);
  }

  public onMessage(callback: (message: string, isUser: boolean) => void): void {
    this.onMessageCallback = callback;
  }

  public onDisconnect(callback: (conversationId: string | null) => Promise<void>): void {
    this.onDisconnectCallback = callback;
  }

  // Fired when the ElevenLabs agent invokes the "requestHumanHandoff" client
  // tool mid-conversation. Requires that tool to be declared on the agent's
  // ElevenLabs dashboard config; harmless no-op until then.
  public setOnHumanHandoffRequested(callback: () => void): void {
    this.onHumanHandoffRequestedCallback = callback;
  }

  // Fired when the Genie invokes the "requestContactDetails" client tool on a
  // visitor's buying intent (attached while ad conversions are on, see
  // handle-agent's syncContactDetailsTool). The handler shows the contact form,
  // prefilled with what the Genie heard, and says how it went.
  public setOnContactDetailsRequested(callback: ContactDetailsRequestHandler | null): void {
    this.onContactDetailsRequestedCallback = callback;
  }

  private requestContactDetails(parameters: Record<string, unknown>): string {
    if (!this.onContactDetailsRequestedCallback) return VoiceAgentService.CONTACT_DETAILS_REPLIES.unavailable;

    const prefill: Partial<SummaryCaptureValues> = {};
    for (const field of ["name", "email", "phone"] as const) {
      const value = parameters?.[field];
      if (typeof value !== "string") continue;
      const trimmed = value.trim().slice(0, VoiceAgentService.CONTACT_DETAIL_MAX_LENGTH[field]);
      if (trimmed) prefill[field] = trimmed;
    }

    return VoiceAgentService.CONTACT_DETAILS_REPLIES[this.onContactDetailsRequestedCallback(prefill)];
  }

  // Receives each link/picture the Genie puts on screen via showRelatedMedia.
  // With `replacesUrl` the card takes the place of the one showing that URL.
  // Kept apart from onMessage so surfaces without a screen for it (setup
  // advisors, quick tests) never see empty transcript entries; without a
  // listener the tool answers "not_found" and the Genie carries on.
  public onAttachment(callback: ((attachment: GenieAttachment, replacesUrl?: string) => void) | null): void {
    this.onAttachmentCallback = callback;
  }

  // showRelatedMedia client tool: the agent names a topic, the resolve-media
  // action picks what to show. The JSON reply lands in the agent's context and
  // in the stored transcript's tool_results, where the dashboard reads it back.
  private async showRelatedMedia(parameters: { topic?: unknown; context?: unknown }): Promise<string> {
    const topic = typeof parameters?.topic === "string" ? parameters.topic.trim() : "";
    const context = typeof parameters?.context === "string" ? parameters.context.trim() : null;
    if (
      !topic ||
      !this.onAttachmentCallback ||
      this.mediaRequestCount >= VoiceAgentService.MAX_MEDIA_REQUESTS_PER_SESSION
    ) {
      return VoiceAgentService.MEDIA_NOT_FOUND;
    }
    this.mediaRequestCount++;

    const runningLookup = mediaLookups.begin();
    const session = this.conversation;
    const request = { agentId: this.agentId, topic, context, transcript: this.recentTurns };
    const resolve = (stage?: "own" | "documents") =>
      ConversationService.resolveMedia({ ...request, stage })
        .then((result) =>
          result.items.filter((item) => isHttpUrl(item.url) && (!item.imageUrl || isHttpUrl(item.imageUrl))),
        )
        .catch((error) => {
          console.warn("[VoiceAgent] showRelatedMedia failed:", error);
          return [] as GenieAttachment[];
        });

    // Three requests, quickest first: the genie's own Resources and knowledge
    // base, then the full lookup that also searches the Resources' sites and
    // the web, and what is written inside the genie's documents. Each one
    // adds what it finds as it lands, minus anything already found.
    const ownLookup = resolve("own");
    const fullLookup = resolve();
    const documentLookup = resolve("documents");
    // The agent is answered with the first results there are.
    const firstLookup = ownLookup.then((items) => (items.length > 0 ? items : fullLookup));

    // The chat keeps the best few cards of this share: a later, better pick
    // takes the weakest card's place. Everything found goes to the Resources sheet.
    const foundUrls = new Set<string>();
    const onScreen: GenieAttachment[] = [];
    const show = (items: GenieAttachment[]): { shown: GenieAttachment[]; replaced: GenieAttachment[] } => {
      const shown: GenieAttachment[] = [];
      const replaced: GenieAttachment[] = [];
      if (this.conversation !== session) return { shown, replaced };
      const fresh = items.filter((item) => !foundUrls.has(item.url)).sort(compareAttachments);
      fresh.forEach((item) => foundUrls.add(item.url));
      foundMedia.add(fresh);
      for (const item of fresh) {
        if (onScreen.length < VoiceAgentService.MAX_MEDIA_IN_CHAT) {
          onScreen.push(item);
          this.onAttachmentCallback?.(item);
          shown.push(item);
          continue;
        }
        const weakest = onScreen.reduce((worst, card) => (compareAttachments(card, worst) > 0 ? card : worst));
        if (compareAttachments(item, weakest) >= 0) continue;
        onScreen[onScreen.indexOf(weakest)] = item;
        this.onAttachmentCallback?.(item, weakest.url);
        shown.push(item);
        replaced.push(weakest);
      }
      if (shown.length > 0) runningLookup.shown();
      return { shown, replaced };
    };
    // For results that land after the agent was answered: shown, and the agent told what changed.
    const showLate = (items: GenieAttachment[]) => {
      const { shown, replaced } = show(items);
      if (shown.length > 0) this.sendContextualUpdate(formatLateMediaUpdate(shown, replaced));
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const items = await Promise.race([
      firstLookup,
      new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => resolve(null), VoiceAgentService.MEDIA_WAIT_MS);
      }),
    ]);
    clearTimeout(timeoutId);
    const shown = items ? show(items).shown : [];

    const afterFirst = firstLookup.then(showLate);
    const lateLookups = [fullLookup, documentLookup];
    let lateLookupsLeft = lateLookups.length;
    lateLookups.forEach((lateLookup) => {
      void afterFirst
        .then(() => lateLookup)
        .then((lateItems) => {
          // Settled in the same step as the last items land, so the
          // placeholder card is swapped for them rather than vanishing first.
          try {
            showLate(lateItems);
          } finally {
            if (--lateLookupsLeft === 0) runningLookup.settled();
          }
        });
    });

    // Too slow to hold the agent for: whatever is found still lands on screen.
    if (items === null) return VoiceAgentService.MEDIA_STILL_LOOKING;
    return shown.length > 0 ? JSON.stringify({ status: "shown", items: shown }) : VoiceAgentService.MEDIA_NOT_FOUND;
  }

  private clearAllModeTimeouts(): void {
    if (this.thinkingTimeout) {
      clearTimeout(this.thinkingTimeout);
      this.thinkingTimeout = null;
    }
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }
    if (this.searchingTimeout) {
      clearTimeout(this.searchingTimeout);
      this.searchingTimeout = null;
    }
    if (this.retrievingTimeout) {
      clearTimeout(this.retrievingTimeout);
      this.retrievingTimeout = null;
    }
  }

  private updateMode(mode: ConversationMode | { mode: string }): void {
    // Extract the mode string if it's an object
    const modeValue = typeof mode === "object" && mode.mode ? mode.mode : mode;

    // Valid modes including new escalation states
    const validModes = ["idle", "speaking", "listening", "thinking", "processing", "searching", "retrieving"];

    // Only proceed if it's a valid mode
    if (!validModes.includes(modeValue as string)) {
      console.log(`Unknown mode: ${JSON.stringify(mode)}`);
      return;
    }

    // Now we can safely cast it
    const actualMode = modeValue as ConversationMode;

    if (this.currentMode !== actualMode) {
      console.log(`Mode changed: ${this.currentMode} -> ${actualMode}`);
      this.currentMode = actualMode;
      if (this.onModeChangeCallback) this.onModeChangeCallback(actualMode);
    }
  }

  private updateStatus(status: ConversationStatus | { status: string }): void {
    // Extract the status string if it's an object
    const statusValue = typeof status === "object" && status.status ? status.status : status;

    // Only proceed if it's a valid status
    if (statusValue !== "disconnected" && statusValue !== "connecting" && statusValue !== "connected") {
      //console.log(`Unknown status: ${JSON.stringify(status)}`);
      return;
    }

    // Now we can safely cast it
    const actualStatus = statusValue as ConversationStatus;

    if (this.currentStatus !== actualStatus) {
      //console.log(`Status changed: ${this.currentStatus} -> ${actualStatus}`);
      this.currentStatus = actualStatus;
      if (this.onStatusChangeCallback) this.onStatusChangeCallback(actualStatus);
    }
  }

  private startKeepAlive(): void {
    // Clear any existing interval
    this.stopKeepAlive();

    // Send a small ping every 30 seconds to keep connection alive
    this.keepAliveInterval = setInterval(() => {
      if (this.conversation && this.currentStatus === "connected") {
        console.log("Keep-alive ping");
        // Send empty contextual update to maintain connection
        try {
          this.conversation.sendContextualUpdate("");
        } catch (error) {
          console.warn("Keep-alive ping failed:", error);
        }
      }
    }, 30000); // 30 seconds
  }

  private stopKeepAlive(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }
}

const isHttpUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

declare global {
  interface Window {
    persistedAudioStream?: MediaStream;
  }
}
